import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import AdminPage from '../components/AdminShell';

// The AI trading cards the nightly autofill run (and imports) made, each beside the photo it was
// made from, so staff can spot a bad one and remove it. Removing records a staff event server-side
// so the nightly run never remakes that card. Staff only.
// Backend: GET / DELETE /api/admin/ai-cards (backend/app/routers/admin_ai_cards.py).

const LIMIT = 50;
// A confirming click inside this window after the button turned into "Remove for good?" is ignored,
// so a fast double-click (React re-renders between its two clicks) cannot delete without a real
// second decision. Removal cannot be undone and stops the nightly run remaking the card.
const CONFIRM_GUARD_MS = 400;
const THUMB = { width: 120, height: 168, objectFit: 'cover', borderRadius: 6, display: 'block' };
const PLACEHOLDER = {
  ...THUMB, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
  background: '#f1f3f5', color: '#6b7280', fontSize: 12, padding: 8, boxSizing: 'border-box',
};

const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Server timestamps are UTC without a tz suffix; parse as UTC, show local.
const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(/([zZ])|([+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`);
  return `${d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}, `
    + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

function CardRow({ card, onRemove }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const removeRef = useRef(null);
  const confirmAt = useRef(0);
  const who = card.athlete_name || `card #${card.card_id}`;

  const cancel = () => {
    setConfirming(false);
    // The Cancel button unmounts; put keyboard focus back on the button it sat beside.
    if (removeRef.current) removeRef.current.focus();
  };

  // One button that changes label, so keyboard focus stays on it between the two steps.
  const onRemoveClick = async (e) => {
    if (!confirming) {
      confirmAt.current = Date.now();
      setConfirming(true);
      setError('');
      return;
    }
    // The confirming click must be its own deliberate click: not the second half of a double-click,
    // and not within CONFIRM_GUARD_MS of the first.
    if (e.detail > 1 || Date.now() - confirmAt.current < CONFIRM_GUARD_MS) return;
    setBusy(true);
    const ok = await onRemove(card.card_id);
    if (!ok) {
      setBusy(false);
      setConfirming(false);
      setError("Couldn't remove. Try again.");
    }
  };

  return (
    <tr>
      <td>
        {card.source_photo_url
          ? <img src={card.source_photo_url} alt={`Original upload for ${who}`} style={THUMB} />
          : <div style={PLACEHOLDER}>No photo preview</div>}
      </td>
      <td><img src={card.card_url} alt={`AI card for ${who}`} style={THUMB} /></td>
      <td>
        <Link className="adm-link" to={`/admin/athletes/${card.user_id}`}>{card.athlete_name}</Link>
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(card.created_at)}</td>
      <td>{card.source}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <button
          ref={removeRef}
          type="button"
          className={`adm-btn adm-btn--sm ${confirming ? 'adm-btn--danger' : ''}`}
          onClick={onRemoveClick}
          disabled={busy}
        >
          {confirming ? 'Remove for good?' : 'Remove'}
        </button>
        {confirming && (
          <button type="button" className="adm-btn adm-btn--sm adm-btn--ghost" style={{ marginLeft: 6 }}
            onClick={cancel} disabled={busy}>
            Cancel
          </button>
        )}
        {error && <div className="adm-error" role="alert" style={{ marginTop: 6 }}>{error}</div>}
      </td>
    </tr>
  );
}

export default function AdminAiCardsPage() {
  const { API_URL } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    axios.get(`${API_URL}/admin/ai-cards`, { ...authConfig(), params: { limit: LIMIT } })
      .then((res) => { if (!cancelled) setCards(Array.isArray(res.data) ? res.data : []); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.detail || 'Failed to load AI cards.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [API_URL]);

  // Resolves true when the card is gone (row removed), false when the row should stay.
  const removeCard = useCallback(async (cardId) => {
    try {
      await axios.delete(`${API_URL}/admin/ai-cards/${cardId}`, authConfig());
      setCards((prev) => prev.filter((c) => c.card_id !== cardId));
      return true;
    } catch (err) {
      // 404: the card is already gone (removed in another tab, or no longer an AI card). Drop the row.
      if (err.response?.status === 404) {
        setCards((prev) => prev.filter((c) => c.card_id !== cardId));
        return true;
      }
      return false;
    }
  }, [API_URL]);

  return (
    <AdminPage active="ai-cards" title="AI Cards"
      subtitle={`The newest ${LIMIT} AI trading cards, each beside the photo it was made from. Remove a bad one and the nightly run will not remake it.`}>
      {error && <div className="adm-error">{error}</div>}
      {loading ? (
        <div className="adm__card"><div className="adm-loading">Loading AI cards…</div></div>
      ) : (
        <div className="adm__card adm__card--flush">
          <div className="adm-tablewrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Source photo</th>
                  <th>AI card</th>
                  <th>Athlete</th>
                  <th>Made</th>
                  <th>Source</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {!error && cards.length === 0 && (
                  <tr><td colSpan={6}><div className="adm-empty">No AI cards yet.</div></td></tr>
                )}
                {cards.map((c) => <CardRow key={c.card_id} card={c} onRemove={removeCard} />)}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminPage>
  );
}
